// =============================================================================
// MeuDriver — InstructorFeedCard
//
// Lista os posts recentes do instrutor. Usado em InstructorProfilePage.tsx.
// Conteúdo limitado a 500 chars (controlado no banco via CHECK).
// =============================================================================

import { Image, Rss } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";

interface InstructorFeedCardProps {
  instructorId: string;
}

interface PostRow {
  id: string;
  content: string;
  image_url: string | null;
  created_at: string;
}

const POSTS_PER_PAGE = 5;

async function fetchInstructorPosts(instructorId: string): Promise<PostRow[]> {
  const { data, error } = await supabase
    .from("instructor_posts")
    .select("id, content, image_url, created_at")
    .eq("instructor_id", instructorId)
    .order("created_at", { ascending: false })
    .limit(20);

  if (error) {
    console.error("[InstructorFeedCard] fetch error:", error.message);
    return [];
  }
  return (data ?? []) as PostRow[];
}

function PostCard({ post }: { post: PostRow }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-3">
      {post.image_url && !imgError && (
        <div className="rounded-lg overflow-hidden bg-gray-100 max-h-64">
          <img
            src={post.image_url}
            alt="Imagem do post"
            className="w-full object-cover max-h-64"
            onError={() => setImgError(true)}
          />
        </div>
      )}
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
        {post.content}
      </p>
      <p className="text-xs text-muted-foreground">
        {formatDistanceToNow(parseISO(post.created_at), {
          addSuffix: true,
          locale: ptBR,
        })}
      </p>
    </div>
  );
}

export function InstructorFeedCard({ instructorId }: InstructorFeedCardProps) {
  const [visible, setVisible] = useState(POSTS_PER_PAGE);

  const { data: posts, isLoading } = useQuery({
    queryKey: ["instructor-posts", instructorId],
    queryFn: () => fetchInstructorPosts(instructorId),
    staleTime: 1000 * 60 * 2,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-24 rounded-xl border border-gray-100 bg-gray-50 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-10 text-center">
        <Rss className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="font-medium text-gray-600">Nenhuma novidade ainda</p>
        <p className="text-sm text-gray-400 mt-1">
          O instrutor ainda não publicou nada.
        </p>
      </div>
    );
  }

  const visiblePosts = posts.slice(0, visible);
  const hasMore = visible < posts.length;

  return (
    <div className="space-y-4">
      <h2 className="font-semibold text-gray-900 flex items-center gap-2">
        <Rss className="h-5 w-5 text-brand-600" />
        Novidades
      </h2>
      {visiblePosts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}
      {hasMore && (
        <button
          type="button"
          onClick={() => setVisible((v) => v + POSTS_PER_PAGE)}
          className="w-full py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all"
        >
          Ver mais novidades
        </button>
      )}
    </div>
  );
}

// ─── Subcomponente: Ícone de imagem ausente ────────────────────────────────────

export { Image as FeedImagePlaceholder };
